import config from 'config';
import { DEFAULT_WEEKLY_CAPACITY } from 'constants/account';
import { EMPTY_ARRAY } from 'constants/defaults';
import { MEMORY_RETENTION_ENABLED_WORKSPACE_IDS } from 'constants/memory-retention';
import { isEqual } from 'lodash-es';
import type { CompanySizes, DeprecatedCompanySizes } from 'redux/modules/accounts.schema';
import type {
  AccountReportSettingsDraft,
  AccountReportSettingsPayload,
} from 'redux/modules/accounts.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Account, Currency, Features, StartOfWeek, SubscriptionPlan } from 'types/account';
import { AccountStatus } from 'types/account';
import debug from 'utils/debug';
import { DEFAULT_WEEKDAYS } from 'utils/summary';
import type { Weekdays } from 'utils/work-days';
import { weekdayToFullDayMap } from 'utils/work-days';
import {
  convertDeprecatedCompanySizes,
  filterHourColumnOptions,
  getAccumulatedFeaturesByPlan,
} from './utils/accounts';

const readEmulatedProperties = (state: IState) => state.accounts.emulatedProperties;
const readCurrentAccount = (state: IState) => state.accounts.current;
const readAccountReportSettings = (state: IState) => state.accounts.accountReportSettings;
const readAccountReportSettingsDraft = (state: IState) => state.accounts.accountReportSettingsDraft;
const readAccountUserPropertiesDraft = (state: IState) => state.accounts.accountUserPropertiesDraft;
const readAccountUserProperties = (state: IState) => state.accounts.accountUserProperties;
const readUserPropertiesPreviouslyUsedValues = (state: IState) =>
  state.accounts.userPropertiesPreviouslyUsedValues;
const readUserAccountProperties = (state: IState) => state.accounts.userAccountProperties;
const readAccountOnboardingProperties = (state: IState) => state.accounts.onboardingProperties;
const readSubscriptionPlans = (state: IState) => state.accounts.subscriptionPlans;
const readSubscriptionAddOns = (state: IState) => state.accounts.subscriptionAddOns;
const readAddOnScopeKey = (state: IState) => state.subscriptions.addOnScopeKey;
const readResourcePermissions = (state: IState) => state.accounts.resourcePermissions;
const readResourcePermissionsDraft = (state: IState) => state.accounts.resourcePermissionDraft;
const readXhrStatus = (state: IState) => state.accounts.xhrStatus;

export const getAccountDraft = (state: IState) => state.accounts.draft;
export const getOnboardingDraft = (state: IState) => state.accounts.onboardingDraft;
export const getShowAddOnsModal = (state: IState) => !!state.subscriptions.addOnScopeKey;
export const getShowBookDemoModal = (state: IState) => state.accounts.isShowingBookDemoModal;
// circular deps :/
const readUserHideInternalRate = (state: IState) => state.user.hide_internal_hourly_rate;

export const getXhrStatus = readXhrStatus;

export const getXhrStatusUIState = createSelector(
  [getXhrStatus],
  xhrStatus => xhrStatusUIState(xhrStatus),
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getAccountSaving = createSelector(
  [getXhrStatusUIState],
  xhrStatus => xhrStatus.isSaving
);

export const getAccountLoading = createSelector(
  [getXhrStatusUIState],
  xhrStatus => xhrStatus.isLoading
);

export const getAccountErrors = (state: IState) => {
  const errors = state.accounts.xhrStatus.errors.map(e => e.error);
  return errors.length ? errors : EMPTY_ARRAY;
};

export const getAccountOnboardingProperties = readAccountOnboardingProperties;
export const getUserPropertiesPreviouslyUsedValues = readUserPropertiesPreviouslyUsedValues;

export const getDraftAccountUserProperties = readAccountUserPropertiesDraft;
export const getAccountUserProperties = readAccountUserProperties;
export const getUserAccountProperties = readUserAccountProperties;

export const getIsDirectSalesLead = createSelector([getAccountOnboardingProperties], properties => {
  const companySize = properties?.company_size?.value! as
    | CompanySizes
    | `${DeprecatedCompanySizes}`;
  return convertDeprecatedCompanySizes(companySize) > 10;
});

export const getAccountFeatures = createSelector(
  [readCurrentAccount, readEmulatedProperties],
  (account, emulatedFeatures) => emulatedFeatures?.features ?? account.features
);

export const getAccountFeature = createSelector(
  [getAccountFeatures, (_: IState, feature: keyof Features) => feature],
  (features, feature) => {
    return !!features[feature];
  }
);

export const getCurrentAccountTasksColumnOrder = createSelector(
  [readAccountReportSettings],
  settings => settings?.tasks_column_order || EMPTY_ARRAY
);

export const getCurrentAccountColumnOrder = createSelector(
  [readCurrentAccount, readAccountReportSettings, readUserHideInternalRate],
  (account, settings, hideInternalRate) =>
    settings?.hours_column_order
      ? filterHourColumnOptions(settings.hours_column_order, {
          hideInternalRate,
          hasTasks: !!account.features.tasks,
        })
      : EMPTY_ARRAY
);
export const getCurrentAccountTaskColumnOrder = createSelector(
  [readAccountReportSettings],
  account => account?.tasks_column_order || EMPTY_ARRAY
);

export const getCurrentAccountColumnOrders = createSelector(
  [getCurrentAccountTasksColumnOrder, getCurrentAccountColumnOrder],
  (tasksColumnOrder, hoursColumnOrder) => ({
    tasksColumnOrder,
    hoursColumnOrder,
  })
);

export const getDraftAccountColumnOrder = createSelector(
  [readAccountReportSettingsDraft],
  draft => draft?.hours_column_order || EMPTY_ARRAY
);

export const getDraftAccountTaskColumnOrder = createSelector(
  [readAccountReportSettingsDraft],
  draft => draft?.tasks_column_order || EMPTY_ARRAY
);

export const getAccountReportSettings = createSelector(
  [readAccountReportSettings, getCurrentAccountColumnOrder],
  (settings, reportColumns) =>
    ({
      ...settings,
      hours_column_order: reportColumns,
    }) as AccountReportSettingsPayload
);
export const getAccountReportSettingsDraft = createSelector(
  [readAccountReportSettingsDraft, getDraftAccountColumnOrder],
  (draft, reportColumns) =>
    ({
      ...draft,
      hours_column_order: reportColumns,
    }) as AccountReportSettingsDraft
);

export const getCurrentAccount = createSelector(
  [readCurrentAccount, readEmulatedProperties, getAccountFeatures],
  (account, emulatedProperties, features): Account => {
    return {
      ...account,
      beta: emulatedProperties?.beta ?? account.beta,
      features,
      experiments: emulatedProperties?.experiments ?? account.experiments,
      plan_code: emulatedProperties?.plan?.code ?? account.plan_code,
      plan_id: emulatedProperties?.plan?.id ?? account.plan_id,
      plan_name: emulatedProperties?.plan?.name ?? account.plan_name,
      plan_custom: emulatedProperties?.plan_custom ?? account.plan_custom,
      status: emulatedProperties?.status ?? account.status,
      next_charge: emulatedProperties?.next_charge ?? account.next_charge,
      trial:
        emulatedProperties?.status && emulatedProperties?.status === 'trial'
          ? emulatedProperties?.status === 'trial'
          : account.trial,
    } as Account;
  },
  {
    memoizeOptions: {
      // Deep equal this one as it triggers a lot of re-evaluations when the account changes
      resultEqualityCheck: isEqual,
    },
  }
);

export const getCurrentAccountId = createSelector([getCurrentAccount], account => account.id);

export const getAccountOwnerId = createSelector([getCurrentAccount], account => account.owner_id);

export const getAccountSupportEmail = createSelector(
  [getCurrentAccount],
  account => account.support_email || 'support@timelyapp.com'
);

const readHideIntercomLauncher = () => window.BOOTSTRAPPED_DATA.hide_intercom_launcher;

export const getHideIntercomLauncher = readHideIntercomLauncher;

export const getStartOfWeek = createSelector(
  [getCurrentAccount],
  (account): StartOfWeek => account.start_of_week
);

const readWeekdays = () => Object.keys(weekdayToFullDayMap) as Array<Weekdays>;

export const getAccountAvailableWeekdays = createSelector(
  [getAccountDraft, getStartOfWeek, readWeekdays],
  (draft, startOfWeek, weekdays) => {
    const start = draft?.start_of_week ?? startOfWeek;
    // Handle 0, 1, 6 (Sunday, Monday, Saturday) as start of week
    // weekdays is in order sun -> mon
    return [...weekdays.slice(start), ...weekdays.slice(0, start)];
  }
);

export const getAccountWeekdays = createSelector(
  [getAccountDraft, getCurrentAccount],
  (draft, account) => {
    return draft?.default_work_days ?? account?.default_work_days ?? DEFAULT_WEEKDAYS;
  }
);

export const getAccountWeekdaysList = createSelector([getAccountWeekdays], weekdays => {
  return weekdays.split(',');
});

export const getAccountCurrency = createSelector(
  [getCurrentAccount],
  (account): Currency => account.currency
);

export const getAccountExperiments = createSelector(
  [getCurrentAccount],
  (account): Record<string, string> => {
    if (!account.experiments) {
      return {};
    }
    return account.experiments.reduce((map: Record<string, string>, experiment) => {
      map[experiment.name] = experiment.value;
      return map;
    }, {});
  }
);

export const getNumberOfTotalProjectsInWorkspace = createSelector(
  [getCurrentAccount],
  account => account.total_projects_count
);
export const getNumberOfActiveProjectsInWorkspace = createSelector(
  [getCurrentAccount],
  account => account.active_projects_count
);

export const getRequiredFieldsFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.project_required_fields
);

export const getTeamsFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.teams
);

export const getAnomaliesFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features) => !!features.anomalies
);

export const getIsChatFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features) => !!features.in_app_support
);

export const getIsInvoicesFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features) => !!features.invoices
);

export const getArePremiumIntegrationsEnabled = createSelector(
  [getAccountFeatures],
  features => !!features.premium_integrations
);

export const getAreAzureADIntegrationEnabled = createSelector(
  [getCurrentAccount],
  account => account.azure_ad_enabled
);

export const getIsMemoryRetentionEnabled = createSelector(
  [getCurrentAccountId, readEmulatedProperties],
  (currentAccountId, emulatedProperties) => {
    const isWorkspaceAllowed = MEMORY_RETENTION_ENABLED_WORKSPACE_IDS.includes(currentAccountId);
    const isEmulatingFeature = !!emulatedProperties?.features?.memory_retention;
    return isWorkspaceAllowed || isEmulatingFeature;
  }
);

export const getIsHighlevelReportsFeatureEnabled = createSelector(
  [getAccountFeatures],
  features => !!features.high_level_reports
);

export const getAccountCanAccessBeta = createSelector([getCurrentAccount], account => account.beta);

export const getRecurringBudgetsEnabled = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.recurring_budget
);

export const getAccountStatus = createSelector(
  [getCurrentAccount],
  (currentAccount): AccountStatus => {
    return currentAccount.status;
  }
);

export const getIsActiveTrial = createSelector(
  [getAccountStatus],
  (status): boolean => status === AccountStatus.trial
);

export const getIsTrialEnded = createSelector(
  [getAccountStatus],
  (status): boolean => status === AccountStatus.trial_expired
);

export const getIsSubscriptionExpired = createSelector([getAccountStatus], (status): boolean => {
  return status === AccountStatus.expired;
});

export const getIsSubscriptionExpiredOrTrialEnded = createSelector(
  [getIsTrialEnded, getIsSubscriptionExpired],
  (isTrialEnded, isSubscriptionExpired): boolean => {
    return isTrialEnded || isSubscriptionExpired;
  }
);

export const getHasNoProjectLimit = createSelector(
  [getCurrentAccount],
  account => account.max_projects === 0
);

export const getNumberOfAvailableProjects = createSelector([getCurrentAccount], account => {
  const availableProjects = account.max_projects - account.total_projects_count;
  return availableProjects > 0 ? availableProjects : 0;
});

export const getAccountMemoryHistoryDays = createSelector(
  [getAccountFeatures],
  (features): number => features.memory_history_days ?? 0
);

export const getIsEmulatingFeatures = createSelector(
  [readEmulatedProperties],
  emulatedFeatures => !!emulatedFeatures
);

/**
 * Gets the weekly capacity of the current account.
 * @default 40
 */
export const getAccountWeeklyCapacity = createSelector(
  [readCurrentAccount],
  account => account.weekly_user_capacity ?? DEFAULT_WEEKLY_CAPACITY
);

export const getAccountIsOnOldPlan = createSelector(
  [getCurrentAccount, readSubscriptionPlans],
  (account, plans) => !plans.find(plan => plan.code === account.plan_code)
);

export const getAccountIsOnMonthlyPlan = createSelector(
  [getCurrentAccount, readSubscriptionPlans],
  (account, plans) =>
    plans.find(plan => plan.code === account.plan_code)?.billing_cycle === 'monthly'
);

export const getSubscriptionPlans = readSubscriptionPlans;

export const getSubscriptionFeaturesByPlan = createSelector(
  [getSubscriptionPlans],
  getAccumulatedFeaturesByPlan
);

export const getSubscriptionAddOns = readSubscriptionAddOns;

export const getScopedSubscriptionAddOns = createSelector(
  [getSubscriptionAddOns, readAddOnScopeKey],
  (addOns, scopeKey) => {
    if (!scopeKey) {
      return null;
    }

    return addOns.find(addOn => addOn.name === scopeKey);
  }
);

export const getSubscriptionPlansGroupedByBillingCycle = createSelector(
  [readSubscriptionPlans],
  plans =>
    plans.reduce(
      (groupedPlans, plan) => {
        if (!groupedPlans[plan.name]) {
          groupedPlans[plan.name] = {} as Record<
            SubscriptionPlan['billing_cycle'],
            SubscriptionPlan
          >;
        }

        groupedPlans[plan.name][plan.billing_cycle] = plan;

        return groupedPlans;
      },
      {} as Record<string, Record<SubscriptionPlan['billing_cycle'], SubscriptionPlan>>
    )
);

export const getTaskAddOnInTrial = createSelector([getSubscriptionAddOns], addOns => {
  return (
    addOns.find(addOn => addOn.name.includes('Tasks') && addOn.status === AccountStatus.trial) ||
    null
  );
});

export const getHasReachedUserLimit = createSelector([getCurrentAccount], account => {
  if (account.max_users === 0) {
    return false; // unlimited
  }
  return account.num_users >= account.max_users;
});

export const getRemainingSeats = createSelector([getCurrentAccount], account => {
  if (isNaN(account.seats)) {
    return 0;
  }
  return Math.max(account.seats - account.num_users, 0);
});

export const getHasReachedSeatsLimit = createSelector([getRemainingSeats], freeSeats => {
  return freeSeats === 0;
});

export const getCompanySize = createSelector([getCurrentAccount], account => {
  return convertDeprecatedCompanySizes(account.company_size);
});

export const getBookDemoLink = createSelector(
  [getAccountStatus, getCompanySize],
  (status, companySize): { external: string; embed: string } | null => {
    const hasTrial = status === AccountStatus.trial || status === AccountStatus.trial_expired;

    if (hasTrial) {
      if (companySize <= 1) {
        return null;
      } else if (companySize <= 30) {
        return config.bookDemoUrl.smallCompanies;
      } else if (companySize > 30) {
        return config.bookDemoUrl.largeCompanies;
      }
    }

    return null;
  }
);

export const getNumberOfUsersInAccount = (state: IState) => state.accounts.current.num_users;

export const getHasHourStatesFeature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.hour_states
);

export const getHasBillingFeature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.billing
);

// avoiding circular dependency 🫠
const readUserId = (state: IState) => state.user.id;
// @link https://linear.app/timelyapp/issue/TT-3057/[service-request]-individual-access-to-new-timeline
const TIC_WHITELISTED_USER_IDS = [2231206, 2278323, 1965544];
export const getHasTicWorkspaceSupport = createSelector(
  [getAccountFeatures, readUserId],
  (features, userId): boolean => !!features.tic_support || TIC_WHITELISTED_USER_IDS.includes(userId)
);

export const getHasTicAiFeature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.ai_timesheet_creation
);

export const getHasTicForcedEnabled = createSelector(
  [getCurrentAccount],
  ({ tic_force_enable }): boolean => !!tic_force_enable
);

/**
 * @warning This feature doesn't exists, so fake it on during development.
 * @note It should very likely become a real feature, though.
 */
export const getHasTicIntegrationsSupport = createSelector(
  [getAccountFeatures],
  (features: Features) => {
    const hasFeature = !!features.tic_integrations;
    const isDevelopment = !!__DEVELOPMENT__;
    if (!hasFeature && isDevelopment) {
      debug.template.devEnvOnly(
        'Forcing `tic_integrations` on during dev, account does NOT have it enabled',
        { features }
      );
    }
    return hasFeature || isDevelopment;
  }
);

/**
 * Data export integrations (Google Sheets, etc) - for reporting category integrations.
 * Currently gated by google_sheets feature flag, may expand to support additional integrations.
 */
export const getHasDataExportIntegrationsSupport = createSelector(
  [getAccountFeatures],
  (features: Features) => !!features.google_sheets
);

export const getResourcePermissions = readResourcePermissions;

export const getResourcePermissionsDraft = readResourcePermissionsDraft;

export const getHasResourcePermissionsDraftChanged = createSelector(
  [readResourcePermissionsDraft, getResourcePermissions],
  (draft, permissions) =>
    !!draft && !!permissions && !isEqual(permissions[draft?.resource], draft.permissions)
);

export const getShouldGetNewMemoryVersion = createSelector([getCurrentAccount], account => {
  const WORKSPACES_WITH_ISSUES_WITH_NEW_MEMORY_VERSION = [980768];
  if (WORKSPACES_WITH_ISSUES_WITH_NEW_MEMORY_VERSION.includes(account.id)) {
    return false;
  }
  return true;
});

export const getHasImportManagerFeature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.import_manager
);

export const getUserProjectDistributionFeatureEnabled = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.user_project_distribution
);

export const getHasTimesheetAssistantV2Feature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.timesheet_assistant_v2
);

export const getHideSwitchToClassicFeature = createSelector(
  [getAccountFeatures],
  (features: Features): boolean => !!features.hide_switch_to_classic
);
